"use client";

import cn from "classnames";

import { ChevronLeftIcon } from "@/components/icons/chevronLeft.icon";
import { ChevronRightIcon } from "@/components/icons/chevronRight.icon";
import { PauseIcon } from "@/components/icons/pause.icon";
import { PlayIcon } from "@/components/icons/play.icon";

import { stepIndex } from "./adtSocialProof.utils";

const circleButtonClass = {
  surface:
    "flex size-8 shrink-0 items-center justify-center rounded-full border border-white bg-transparent text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-40",
  media:
    "flex size-8 shrink-0 items-center justify-center rounded-full border border-white/55 bg-black/45 text-white backdrop-blur-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-40",
} as const;

export function AdtSocialProofControls({
  count,
  index,
  onSelect,
  previousLabel,
  nextLabel,
  itemLabel,
  groupLabel,
  labelledBy,
  pause,
  tone = "surface",
  spacing = "compact",
}: AdtSocialProofControlsProps) {
  const canStep = count > 1;
  if (!canStep && !pause) return null;

  const buttonClass = circleButtonClass[tone];

  return (
    <div
      role="group"
      aria-label={groupLabel ?? itemLabel}
      className={cn(
        "flex items-center",
        spacing === "relaxed" ? "justify-start gap-8" : "justify-center gap-8",
      )}>
      {canStep ? (
        <button
          type="button"
          className={buttonClass}
          aria-label={previousLabel}
          onClick={() => onSelect(stepIndex(index, count, false))}>
          <ChevronLeftIcon aria-hidden width={16} height={16} />
        </button>
      ) : null}
      {canStep ? (
        <div className="flex items-center gap-2">
          {Array.from({ length: count }, (_, slide) => {
            const selected = slide === index;
            return (
              <button
                key={slide}
                type="button"
                data-slide-index={slide}
                aria-label={`${itemLabel} ${slide + 1} of ${count}`}
                aria-current={selected ? "true" : undefined}
                aria-controls={labelledBy}
                onClick={() => onSelect(slide)}
                className={cn(
                  "rounded-full bg-white transition-[width] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white motion-reduce:transition-none",
                  selected ? "h-2 w-6" : "size-2",
                )}
              />
            );
          })}
        </div>
      ) : null}
      {canStep ? (
        <button
          type="button"
          className={buttonClass}
          aria-label={nextLabel}
          onClick={() => onSelect(stepIndex(index, count, true))}>
          <ChevronRightIcon aria-hidden width={16} height={16} />
        </button>
      ) : null}
      {pause ? (
        <button
          type="button"
          className={buttonClass}
          aria-pressed={pause.paused}
          aria-label={pause.paused ? pause.resumeLabel : pause.pauseLabel}
          onClick={pause.onToggle}>
          {pause.paused ? (
            <PlayIcon aria-hidden width={16} height={16} />
          ) : (
            <PauseIcon aria-hidden width={16} height={16} />
          )}
        </button>
      ) : null}
    </div>
  );
}

type AdtSocialProofControlsProps = {
  count: number;
  index: number;
  onSelect: (next: number) => void;
  previousLabel: string;
  nextLabel: string;
  itemLabel: string;
  groupLabel?: string;
  labelledBy?: string;
  tone?: "surface" | "media";
  /** Compact keeps arrows close to the dots. Relaxed opens that gap. */
  spacing?: "compact" | "relaxed";
  pause?: {
    paused: boolean;
    pauseLabel: string;
    resumeLabel: string;
    onToggle: () => void;
  };
};
